import React, { useCallback, useState } from "react";
import { Redirect, Route, Switch } from "react-router-dom";

import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import { IconProps, Stack } from "@mui/material";
import { ConsolidationIcon, WorkspaceDocumentIcon } from "assets/material-custom-icons";
import { HorizontalNavBar } from "components";
import { AiData } from "components/AiData/AiData";
import { Consolidation } from "components/Consolidation/Consolidation";
import { DebtPayments } from "components/ExpectedPayments/DebtPayments";
// import { AddFilesModal } from "components/File-repository/Add-files-modal/AddFilesModal";
import { FileRepository } from "components/File-repository/FileRepository";
import { Routes } from "constants/routes";
import { isNull } from "lodash";
import {
  useAccountSelector,
  useDealActions,
  useMonitoringDataFilters,
  usePickUserPermissions,
  useUserActions
} from "store";
import { MeasureDataTableFilters, UserPermissionType } from "store/types";

import { SelectDeal } from "../Select-deal/SelectDeal";
import { DataView } from "./DataView";

const iconsSx: IconProps["sx"] = {
  fontSize: "16px",
  mr: "6px"
};

type RedirectsFrom = keyof Pick<typeof Routes, "monitoring" | "dealMonitoring">;
type DataRoutes = keyof Pick<typeof Routes, "monitoringData" | "dealMonitoringData">;
type FileRepoRoute = keyof Pick<typeof Routes, "fileRepository">;
type ConsolidationRoute = keyof Pick<typeof Routes, "consolidation">;
type DebtPaymentsRoute = keyof Pick<typeof Routes, "debtPayments">;
type AiDataRoute = keyof Pick<typeof Routes, "aiData">;
// type DraftsRoutes = keyof Pick<typeof Routes, "monitoringDrafts" | "dealMonitoringDrafts">;
type DataWideRoute = keyof Pick<typeof Routes, "monitoringDataWide">;

interface Props {
  goToData: () => void;
  goToDataWide?: () => void;
  goToFiles: () => void;
  goToConsolidation: () => void;
  goToDebtPayments: () => void;
  routes: {
    root: {
      from: RedirectsFrom;
      to: DataRoutes;
    };
    data: DataRoutes;
    dataWide?: DataWideRoute;
    files: FileRepoRoute;
    consolidation: ConsolidationRoute;
    debtPayments: DebtPaymentsRoute;
    aiData: AiDataRoute;
  };
  showTopInvestors: boolean;
  setShowTopInvestors: (value: boolean) => void;
}

export const GlobalModule: React.FC<Props> = ({
  goToData,
  goToDataWide,
  goToFiles,
  goToConsolidation,
  goToDebtPayments,
  routes,
  showTopInvestors,
  setShowTopInvestors
}) => {
  const [isSelectDeal, setIsSelectDeal] = useState<((id: number) => void) | null>(null);
  const dealMetrics = usePickUserPermissions("deal_metrics");
  const noAccessPermission = dealMetrics && dealMetrics === UserPermissionType.NO_ACCESS;
  const { show_debt, show_global_investor_filter, show_wide_view } = useAccountSelector() || {};

  const { setMeasureDataTableFiltersAction: setDataFilters } = useDealActions();
  const { setInvestorFilterAction } = useUserActions();

  const dataFilters = useMonitoringDataFilters();

  const updateDataFilters = useCallback(
    (params: Partial<MeasureDataTableFilters> | null) => {
      if (isNull(params)) {
        return setDataFilters(null);
      }
      setDataFilters({ ...dataFilters, ...params });
      if (show_global_investor_filter) {
        if (params?.investors?.map((investor) => investor.name).length) {
          setInvestorFilterAction({ Investor: params?.investors?.map((investor) => investor.name) || [] });
        } else {
          setInvestorFilterAction({});
        }
      }
    },
    [dataFilters, show_global_investor_filter]
  );

  return (
    <>
      <Stack
        sx={{
          width: "100%",
          height: "100%",
          overflowY: "hidden",
          p: "0px",
          gap: "16px"
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <HorizontalNavBar
            tabs={[
              {
                value: "data",
                label: "Data",
                icon: <WorkspaceDocumentIcon sx={{ fill: "rgba(73, 102, 130, 1)", ...iconsSx }} />,
                onClick: goToData,
                hidden: !!noAccessPermission,
                matchRouts: [
                  Routes[routes.data],
                  ...(show_wide_view && routes.dataWide ? [Routes[routes.dataWide]] : [])
                ]
              },
              {
                value: "files",
                label: "Files",
                icon: <InsertDriveFileOutlinedIcon sx={iconsSx} />,
                onClick: goToFiles,
                matchRouts: [Routes[routes.files]]
              },
              {
                value: "debtPayments",
                label: "Debt Payments",
                icon: <PollOutlinedIcon sx={iconsSx} />,
                onClick: goToDebtPayments,
                hidden: !show_debt,
                matchRouts: [Routes[routes.debtPayments]]
              },
              {
                value: "consolidation",
                label: "Securities",
                icon: <ConsolidationIcon sx={{ fill: "#496682", ...iconsSx }} />,
                onClick: goToConsolidation,
                matchRouts: [Routes[routes.consolidation]]
              }
            ]}
          />
        </Stack>
        <Switch>
          <Redirect exact from={Routes[routes.root.from]} to={Routes[routes.root.to]} />
          <Route
            path={Routes[routes.data]}
            render={() => (
              <DataView
                goToData={goToData}
                goToDataWide={show_wide_view && routes.dataWide ? goToDataWide : undefined}
                dataWidePath={show_wide_view && routes.dataWide ? Routes[routes.dataWide] : undefined}
                showTopInvestors={showTopInvestors}
                setShowTopInvestors={setShowTopInvestors}
                setFilters={updateDataFilters}
              />
            )}
          />
          <Route path={Routes[routes.files]} render={() => <FileRepository />} />
          <Route path={Routes[routes.consolidation]} render={() => <Consolidation />} />
          <Route path={Routes[routes.debtPayments]} render={() => <DebtPayments />} />
          <Route path={Routes[routes.aiData]} render={() => <AiData />} />
        </Switch>
      </Stack>
      <SelectDeal onSubmit={isSelectDeal} onCancel={() => setIsSelectDeal(null)} />
    </>
  );
};
